import { GroupChannel } from "@sendbird/chat/groupChannel"
import { useEffect, useMemo } from "react"

import { useSendbird } from "../../../components/sendbird/SBProvider"
import { useCoachingGroupsQuery } from "../../../networking/coachingGroups/queries"
import { useGetAllBillingPeriodsQuery } from "../../../networking/sessions/queries"
import { isInactiveClient } from "../../../util/coachingGroups"
import useAdminPageStore from "../hooks/useAdminpageStore"
import useClientTableFilterStore, {
  SelectedClientStatus,
} from "../hooks/useClientTableFilterStore"

const useClientTable = () => {
  const { data: coachingGroups } = useCoachingGroupsQuery()
  const { selectedClientStatus } = useClientTableFilterStore()
  const {
    selectedCoachProfileId: coachProfileId,
    searchValue,
    debouncedSearchValue,
    setDebouncedSearchValue,
  } = useAdminPageStore()
  const { data: billingPeriods } = useGetAllBillingPeriodsQuery()
  const { getChannelByUrl } = useSendbird()

  // Debounce the search value so it doesn't trigger on every keystroke
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchValue(searchValue)
    }, 500) //500ms delay

    return () => {
      clearTimeout(handler)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue])

  const tableDataByCoach = useMemo(
    () => {
      const filteredCoachingGroups = coachingGroups?.filter(
        (coachingGroup) =>
          debouncedSearchValue ||
          coachingGroup.profiles.some((p) => p.id === coachProfileId)
      )

      return (
        filteredCoachingGroups?.map((coachingGroup) => {
          const groupBillingPeriods =
            coachingGroup && billingPeriods
              ? billingPeriods[coachingGroup.id]
              : null
          const channel = getChannelByUrl(
            coachingGroup.sendbird_channel_url
          ) as GroupChannel

          return {
            channel,
            coachingGroup,
            billingPeriods: groupBillingPeriods,
          }
        }) || []
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [coachingGroups, billingPeriods, coachProfileId, debouncedSearchValue]
  )

  const filteredTableData = useMemo(() => {
    if (!debouncedSearchValue) return tableDataByCoach

    return tableDataByCoach.filter((row) =>
      row.coachingGroup.profiles.some((p) => {
        return (
          p.user.name
            .toLowerCase()
            .includes(debouncedSearchValue.toLowerCase()) ||
          p.user.email
            ?.toLowerCase()
            .includes(debouncedSearchValue.toLowerCase()) ||
          debouncedSearchValue === p.id.toString() ||
          debouncedSearchValue === p.user.id.toString()
        )
      })
    )
  }, [tableDataByCoach, debouncedSearchValue])

  const { numActiveClients, numInactiveClients, tableData } = useMemo(() => {
    const activeClients = filteredTableData.filter(
      (row) => !isInactiveClient(row.coachingGroup)
    )
    const inactiveClients = filteredTableData.filter((row) =>
      isInactiveClient(row.coachingGroup)
    )
    const tableData =
      selectedClientStatus === SelectedClientStatus.ACTIVE
        ? activeClients
        : inactiveClients
    return {
      numActiveClients: activeClients.length,
      numInactiveClients: inactiveClients.length,
      tableData,
    }
  }, [filteredTableData, selectedClientStatus])

  return { tableData, numActiveClients, numInactiveClients }
}

export default useClientTable
